import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Sidebar from "../Dashboard/Sidebar";
import Loader from "../layouts/Loader";
import MetaData from "../layouts/MetaData";
import { getSingleStaff, updateStaff } from "../../actions/staffActions";
import { clearStaffUpdated, clearStaffError } from "../../slices/staffSlice";
import { getdesignations } from "../../actions/designationActions";
import { toInputDate } from "../../utils/formatDate";
import AppDatePicker from "../common/AppDatePicker";

const getFileUrl = (item) => {
    if (!item) return '';
    let url = typeof item === 'string' ? item : (item.file || item.url || '');
    if (!url || typeof url !== 'string') return '';
    if (url.includes('localhost:8000') && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        url = url.replace(/https?:\/\/localhost:8000/, '');
    }
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isDev = (window.location.port === '3000' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));
    const backend = isDev ? 'http://localhost:8000' : '';
    return `${backend}${url.startsWith('/') ? '' : '/'}${url}`;
};

export default function UpdateStaff() {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { loading, staff = {}, isStaffUpdated, error } = useSelector((state) => state.staffState);
    const { designations = [] } = useSelector((state) => state.designationState);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [designation, setDesignation] = useState("");
    const [qualification, setQualification] = useState("BNYS");
    const [phone, setPhone] = useState("");
    const [address, setAddress] = useState("");
    const [parentPhone, setParentPhone] = useState("");
    const [monthlySalary, setMonthlySalary] = useState("");
    const [joiningDate, setJoiningDate] = useState("");
    const [status, setStatus] = useState("active");
    const [remarks, setRemarks] = useState("");
    const [idProof, setIdProof] = useState([]);
    const [resume, setResume] = useState(null);

    useEffect(() => {
        dispatch(getdesignations());
        if (id) {
            dispatch(getSingleStaff(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        if (staff && (String(staff.id) === String(id) || String(staff._id) === String(id))) {
            setName(staff.name || "");
            setEmail(staff.email || "");
            const rawDesig = staff.designation || "";
            const isDoc = rawDesig.toLowerCase().includes("doctor") ||
                          rawDesig.toLowerCase().includes("bnys") ||
                          rawDesig.toLowerCase().includes("physician");
            const baseDesig = rawDesig.includes("(") ? rawDesig.split("(")[0].trim() : rawDesig;
            setDesignation(baseDesig);
            if (isDoc) {
                const extracted = rawDesig.includes("(") ? rawDesig.split("(")[1].replace(")", "").trim() : (staff.qualification || "BNYS");
                setQualification(extracted || "BNYS");
            } else {
                setQualification("");
            }
            setPhone(staff.phone || "");
            setAddress(staff.address || "");
            setParentPhone(staff.parentPhone || "");
            setMonthlySalary(staff.monthlySalary || "");
            setJoiningDate(toInputDate(staff.joiningDate || staff.createdAt));
            setStatus(staff.status || "active");
            setRemarks(staff.remarks || "");
        }
    }, [staff, id]);

    useEffect(() => {
        dispatch(clearStaffUpdated());
        dispatch(clearStaffError());
        return () => {
            dispatch(clearStaffUpdated());
            dispatch(clearStaffError());
        };
    }, [dispatch]);

    useEffect(() => {
        if (isStaffUpdated) {
            toast("Staff details updated successfully!", { type: "success" });
            dispatch(clearStaffUpdated());
            navigate("/admin/staff");
        }
        if (error) {
            toast(error, { type: "error" });
            dispatch(clearStaffError());
        }
    }, [isStaffUpdated, error, dispatch, navigate]);

    const submitHandler = (e) => {
        e.preventDefault();

        const formData = new FormData();
        formData.append("name", name);
        formData.append("phone", phone);
        formData.append("designation", designation);
        formData.append("qualification", qualification);
        formData.append("address", address);
        formData.append("parentPhone", parentPhone);
        formData.append("monthlySalary", monthlySalary || 0);
        formData.append("joiningDate", joiningDate);
        formData.append("status", status);
        formData.append("remarks", remarks);

        idProof.forEach((file) => formData.append("idProof", file));
        if (resume) formData.append("resume", resume);

        dispatch(updateStaff(id, formData));
    };

    return (
        <div className="row g-0">
            <div className="col-12 col-md-2"><Sidebar /></div>
            <div className="col-12 col-md-10 main-content">
                <MetaData title="Edit Staff" />
                {loading ? <Loader /> : (
                    <Fragment>
                        <div className="container my-4">
                            <form onSubmit={submitHandler} className="card shadow p-4" encType="multipart/form-data">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h3 className="mb-0">Edit Staff ({staff.staffId || name})</h3>
                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => navigate("/admin/staff")}>
                                        &larr; Back to Staff
                                    </button>
                                </div>

                                <div className="form-group mb-3">
                                    <label className="fw-semibold">Name</label>
                                    <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} required />
                                </div>

                                <div className="form-group mb-3">
                                    <label className="fw-semibold">Email (Read Only)</label>
                                    <input type="email" className="form-control" value={email} disabled />
                                </div>

                                <div className="form-group mb-3">
                                    <label className="fw-semibold">Designation / Profession</label>
                                    <select 
                                        className="form-control" 
                                        value={designation} 
                                        onChange={(e) => setDesignation(e.target.value)} 
                                        required
                                    >
                                        <option value="">-- Select Designation --</option>
                                        {designations.map((d) => (
                                            <option key={d._id || d.id} value={d.name}>
                                                {d.name}
                                            </option>
                                        ))}
                                        {designation && !designations.some(d => (d.name || '').toLowerCase() === (designation || '').toLowerCase()) && (
                                            <option value={designation}>{designation}</option>
                                        )}
                                    </select>
                                </div>

                                {designation && (
                                    designation.toLowerCase().includes('doctor') ||
                                    designation.toLowerCase().includes('bnys') ||
                                    designation.toLowerCase().includes('physician')
                                ) && (
                                    <div className="form-group mb-3 bg-light p-3 rounded border">
                                        <label className="fw-semibold text-success">
                                            <i className="fa fa-user-md me-1"></i> Doctor Qualification / Degree
                                        </label>
                                        <input 
                                            className="form-control" 
                                            placeholder="e.g. BNYS or BNYS, MD" 
                                            value={qualification} 
                                            onChange={(e) => setQualification(e.target.value)} 
                                        />
                                        <small className="text-muted">
                                            This medical degree (e.g. BNYS) will automatically appear on official prescriptions and diet charts.
                                        </small>
                                    </div>
                                )}

                                <div className="form-group mb-3">
                                    <label className="fw-semibold">Phone</label>
                                    <input className="form-control" value={phone} onChange={(e) => setPhone(e.target.value)} required />
                                </div>

                                <div className="form-group mb-3">
                                    <label className="fw-semibold">Address</label>
                                    <textarea className="form-control" rows="2" value={address} onChange={(e) => setAddress(e.target.value)} required />
                                </div>

                                <div className="form-group mb-3">
                                    <label className="fw-semibold">Parent / Emergency Phone</label>
                                    <input className="form-control" value={parentPhone} onChange={(e) => setParentPhone(e.target.value)} />
                                </div>

                                <div className="form-group mb-3">
                                    <label className="fw-semibold">Monthly Salary (₹)</label>
                                    <input type="number" className="form-control" value={monthlySalary} onChange={(e) => setMonthlySalary(e.target.value)} />
                                </div>

                                <div className="form-group mb-3">
                                    <label className="fw-semibold">Joining Date</label>
                                    <AppDatePicker
                                        label="Joining Date"
                                        value={joiningDate}
                                        onChange={(val) => setJoiningDate(val)}
                                    />
                                </div>

                                <div className="form-group mb-3">
                                    <label className="fw-semibold">Status</label>
                                    <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>

                                <div className="form-group mb-3">
                                    <label className="fw-semibold">Remarks</label>
                                    <textarea className="form-control" rows="2" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                                </div>

                                <div className="form-group mb-3">
                                    <label className="fw-semibold">ID Proof</label>
                                    {staff.idProof && staff.idProof.length > 0 && (
                                        <div className="mb-2">
                                            {staff.idProof.map((f, i) => {
                                                const fileUrl = getFileUrl(f);
                                                if (!fileUrl) return null;
                                                return (
                                                    <a key={i} href={fileUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-secondary me-2 mb-1">
                                                        {f?.fileType === 'pdf' ? '📄' : '🖼️'} Current Proof {i + 1}
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <label className="form-label small text-muted mb-1">Upload Additional ID Proof (Optional)</label>
                                    <input type="file" className="form-control" multiple onChange={(e) => setIdProof(Array.from(e.target.files))} />
                                </div>

                                <div className="form-group mb-4">
                                    <label className="fw-semibold">Resume</label>
                                    {(() => {
                                        const resumeObj = staff.resume && !Array.isArray(staff.resume)
                                            ? staff.resume
                                            : (Array.isArray(staff.resume) && staff.resume.length > 0 ? staff.resume[0] : null);
                                        const resumeUrl = getFileUrl(resumeObj);
                                        return resumeUrl ? (
                                            <div className="mb-2">
                                                <a href={resumeUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                                                    📄 View Current Resume ({resumeObj?.originalName || "Resume"})
                                                </a>
                                            </div>
                                        ) : (
                                            <div className="mb-1 text-muted small">No resume uploaded yet</div>
                                        );
                                    })()}
                                    <label className="form-label small text-muted mb-1">Replace Resume (Optional)</label>
                                    <input type="file" className="form-control" onChange={(e) => setResume(e.target.files[0])} />
                                </div>

                                <button type="submit" className="btn btn-primary shadow-sm py-2">
                                    Update Staff Details
                                </button>
                            </form>
                        </div>
                    </Fragment>
                )}
            </div>
        </div>
    );
}

